/**
 * A geometria do selo de estado — UMA definição, dois desenhos.
 *
 * O mesmo estado aparece no canto do avatar e no menu de estado (SVG, este
 * ficheiro) e por cima da cabeça no mapa 2D (canvas, `officeAvatars.ts`). Chegou
 * a haver TRÊS geometrias — havia também um `.dot` em CSS puro — e três luas
 * diferentes para o mesmo estado era, literalmente, o defeito. Aqui a lua existe
 * uma vez, em unidades do RAIO do disco, e cada sítio limita-se a escalá-la.
 *
 * ── as duas coisas que estavam mal, e são independentes ───────────────────
 *
 * 1. PIXELS A MENOS. O selo era `border: 2.5px` + um `::after` circular por
 *    cima. Num avatar de 32 px o selo tem 10,88 px e a borda FIXA leva-lhe 5
 *    (46%): sobravam 5,88 px de interior e a dentada ficava com 4,41. Duas
 *    caixas arredondadas, cada uma encostada à grelha de pixels por sua conta e
 *    suavizada por sua conta, em ~6 px de lado, não formam duas pontas — dão um
 *    quadrado com um canto comido. Medido em Chrome: SETE pixels âmbar, em L.
 *    Resolve-se com um `<path>` único (um só passe de suavização) e um anel
 *    PROPORCIONAL, que nasce dentro do desenho em vez de ser uma borda em px que
 *    engorda quando o selo encolhe.
 *
 * 2. A SILHUETA ABRIA. Com pixels a mais o crescente já se via — e continuava a
 *    ler-se como estragado. A razão não é o número de pixels: é que `idle` era o
 *    ÚNICO estado com o contorno partido. `online` é um disco fechado; `busy` é
 *    um disco fechado com um traço DENTRO; `invisible`/`offline` são discos
 *    fechados com um buraco DENTRO. Só a lua tinha a dentada a escapar pela
 *    borda, e o escuro da dentada fundia-se com o escuro à volta — é isso que o
 *    olho lê como «uma parte faltando», que foi a queixa, à letra.
 *
 *    Daí a lua ir para DENTRO: disco de cor inteiro, crescente escuro cavado no
 *    meio. Mesma gramática dos outros quatro — contorno fechado, marca por
 *    dentro — e a forma continua a distinguir os estados para quem não distingue
 *    âmbar de verde.
 */

import type { PresenceStatus } from "@greenvoice/shared";

export const STATUS_LABEL: Record<PresenceStatus, string> = {
  ONLINE: "Disponível",
  IDLE: "Ausente",
  BUSY: "Ocupado",
  INVISIBLE: "Invisível",
  OFFLINE: "Offline",
};

/** Um disco com uma dentada circular. As unidades são as que quem chama quiser. */
export interface Lua {
  /** centro do disco */
  ox: number;
  oy: number;
  /** raio do disco */
  raio: number;
  /** centro da dentada */
  cx: number;
  cy: number;
  /** raio da dentada */
  dente: number;
}

/**
 * A lua canónica, em unidades do raio do disco (disco de raio 1 na origem).
 *
 * A ESPESSURA que sobra é o número que decide se aquilo se lê como lua ou como
 * bolacha mordida: `(distância − raio da dentada + raio do disco) / diâmetro`
 * = (0,707 − 0,75 + 1) / 2 ≈ **0,479**. Acima de ~0,55 volta a ser um disco com
 * um trinco; abaixo de ~0,35 o crescente desfaz-se nos tamanhos pequenos.
 */
export const LUA_AUSENTE: Lua = { ox: 0, oy: 0, raio: 1, cx: 0.5, cy: -0.5, dente: 0.75 };

/** Quanto sobra do crescente, em fracção do DIÂMETRO do disco. */
export function espessuraDaLua(l: Lua): number {
  return (Math.hypot(l.cx - l.ox, l.cy - l.oy) - l.dente + l.raio) / (2 * l.raio);
}

/** A mesma lua noutro sítio e noutro tamanho. */
export function escalarLua(l: Lua, ox: number, oy: number, raio: number): Lua {
  const k = raio / l.raio;
  return { ox, oy, raio, cx: ox + (l.cx - l.ox) * k, cy: oy + (l.cy - l.oy) * k, dente: l.dente * k };
}

const n = (v: number): string => String(Number(v.toFixed(3)));

/** Um círculo como caminho SVG: dois arcos de meia volta. */
export function caminhoDoCirculo(cx: number, cy: number, r: number): string {
  return `M${n(cx - r)},${n(cy)}a${n(r)},${n(r)} 0 1,0 ${n(r * 2)},0a${n(r)},${n(r)} 0 1,0 ${n(-r * 2)},0z`;
}

/** Um rectângulo de cantos redondos como caminho SVG, centrado em (cx, cy). */
export function caminhoDaBarra(cx: number, cy: number, larg: number, alt: number, r: number): string {
  const x = cx - larg / 2;
  const y = cy - alt / 2;
  const h = larg - 2 * r;
  const v = alt - 2 * r;
  return (
    `M${n(x + r)},${n(y)}h${n(h)}a${n(r)},${n(r)} 0 0,1 ${n(r)},${n(r)}` +
    `v${n(v)}a${n(r)},${n(r)} 0 0,1 ${n(-r)},${n(r)}` +
    `h${n(-h)}a${n(r)},${n(r)} 0 0,1 ${n(-r)},${n(-r)}` +
    `v${n(-v)}a${n(r)},${n(r)} 0 0,1 ${n(r)},${n(-r)}z`
  );
}

/**
 * O crescente propriamente dito: disco MENOS dentada, em dois arcos.
 *
 * Não dá para fazer isto com dois círculos e `evenodd` — a dentada assoma para
 * fora do disco, e a parte dela que fica de fora ficaria pintada também. Só há
 * uma forma correcta: cortar nos dois pontos onde as circunferências se cruzam e
 * seguir, de cada uma, o arco que FOGE da outra.
 *
 *   a = (R² − r² + d²) / 2d    distância do centro do disco à corda comum
 *   h = √(R² − a²)             meia-corda
 *   P = (centro + a·u) ± h·⊥u  os dois cruzamentos
 *
 * O arco do disco é o que passa ao largo da dentada e o da dentada é o que entra
 * pelo disco — em ambos os casos o que se afasta da direcção `u`, e é por isso
 * que os dois `large-arc` saem da mesma conta com o meio-ângulo.
 */
export function caminhoDaLua(l: Lua): string {
  const dx = l.cx - l.ox;
  const dy = l.cy - l.oy;
  const d = Math.hypot(dx, dy);
  // Sem intersecção própria não há crescente nenhum: ou a dentada não chega ao
  // disco, ou engole-o. O disco inteiro é o degenerado seguro — quem guarda a
  // geometria é o teste, e um `throw` aqui dava um ecrã branco por uma feiura.
  if (d === 0 || d >= l.raio + l.dente || d + l.dente <= l.raio || d + l.raio <= l.dente) {
    return caminhoDoCirculo(l.ox, l.oy, l.raio);
  }
  const a = (l.raio * l.raio - l.dente * l.dente + d * d) / (2 * d);
  const h = Math.sqrt(Math.max(l.raio * l.raio - a * a, 0));
  const ux = dx / d;
  const uy = dy / d;
  const mx = l.ox + a * ux;
  const my = l.oy + a * uy;
  const p1x = mx - h * uy;
  const p1y = my + h * ux;
  const p2x = mx + h * uy;
  const p2y = my - h * ux;
  // Meio-ângulo de cada cruzamento visto do respectivo centro. O arco que se usa
  // é o complementar, portanto é maior do que meia volta exactamente quando o
  // meio-ângulo é menor do que um quarto.
  const grande = (meio: number): 0 | 1 => (meio < Math.PI / 2 ? 1 : 0);
  return (
    `M${n(p1x)},${n(p1y)}` +
    `A${n(l.raio)},${n(l.raio)} 0 ${grande(Math.atan2(h, a))},1 ${n(p2x)},${n(p2y)}` +
    `A${n(l.dente)},${n(l.dente)} 0 ${grande(Math.atan2(h, a - d))},0 ${n(p1x)},${n(p1y)}z`
  );
}

/* ── o selo, em coordenadas do `viewBox` ───────────────────────────────────
 *
 * O anel ocupa o selo todo e o disco de cor fica-lhe dentro, portanto a espessura
 * do anel é uma PROPORÇÃO (12,5% de cada lado) e não uma borda em px. É esta a
 * diferença que faz o selo pequeno sobreviver: aos 12 px o interior fica com 9,
 * onde antes ficava com 5,88.
 */

/** O lado da caixa do `viewBox`. */
export const SELO_CAIXA = 16;
/** Raio do disco de cor, em fracção do raio do anel. */
export const PROPORCAO_DO_DISCO = 0.75;
/**
 * Raio da lua, em fracção do raio do DISCO DE COR.
 *
 * É o que fecha a silhueta: os 25% que sobram são o aro de cor que continua a
 * dar a volta por fora do crescente, e é ele que impede o selo de se ler como
 * partido. Aos 12 px de selo o aro fica com ~1,1 px — abaixo disto desaparece na
 * suavização e a mordida volta a tocar na borda.
 */
export const PROPORCAO_DA_LUA = 0.75;

const MEIO = SELO_CAIXA / 2;
/** Raio do disco de cor, já em unidades do `viewBox`. */
export const RAIO_DO_DISCO = MEIO * PROPORCAO_DO_DISCO;

/** O anel: um disco cheio da cor do fundo, por baixo de tudo o resto. */
export const CAMINHO_DO_ANEL = caminhoDoCirculo(MEIO, MEIO, MEIO);

/** A lua do selo, já escalada para caber DENTRO do disco de cor. */
export const LUA_DO_SELO = escalarLua(LUA_AUSENTE, MEIO, MEIO, RAIO_DO_DISCO * PROPORCAO_DA_LUA);

/**
 * A forma de cada estado, num caminho só, a fechar com `fill-rule="evenodd"`:
 * os buracos deixam ver o anel que está por baixo, que é da cor do fundo.
 *
 * Os cinco partilham a mesma gramática — disco fechado, marca por dentro — e é
 * isso que faz o conjunto ler-se como um conjunto. Nenhum tem o contorno aberto.
 *
 * `INVISIBLE` e `OFFLINE` levam o mesmo anel: quem está invisível vê-se a si
 * próprio como quem não está, que é precisamente o que quer dizer.
 */
export const CAMINHO_DO_SELO: Record<PresenceStatus, string> = {
  ONLINE: caminhoDoCirculo(MEIO, MEIO, RAIO_DO_DISCO),
  IDLE: caminhoDoCirculo(MEIO, MEIO, RAIO_DO_DISCO) + caminhoDaLua(LUA_DO_SELO),
  BUSY:
    caminhoDoCirculo(MEIO, MEIO, RAIO_DO_DISCO) +
    caminhoDaBarra(MEIO, MEIO, RAIO_DO_DISCO * 2 * 0.62, RAIO_DO_DISCO * 2 * 0.22, 0.6),
  INVISIBLE: caminhoDoCirculo(MEIO, MEIO, RAIO_DO_DISCO) + caminhoDoCirculo(MEIO, MEIO, RAIO_DO_DISCO * 0.44),
  OFFLINE: caminhoDoCirculo(MEIO, MEIO, RAIO_DO_DISCO) + caminhoDoCirculo(MEIO, MEIO, RAIO_DO_DISCO * 0.44),
};
